import React, { useEffect, useMemo, useState } from 'react';
import TitleSetter from '../components/TitleSetter';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, limit, query, orderBy, serverTimestamp, updateDoc, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from '../firebaseConfig';
import Overview from './components/Overview';
import Users from './components/Users';
import Teams from './components/Teams';
import Audit from './components/Audit';
import MediaLibrary from './components/MediaLibrary';
import Requests from './components/Requests';
import Tickets from './components/Tickets';
import './SuperAdmin.css';
import { signOut } from 'firebase/auth';
import { toast } from 'react-toastify';

const TABS = ['overview', 'users', 'teams', 'library', 'requests', 'tickets', 'audit'];
const ROLE_OPTIONS = ['superadmin', 'admin', 'coach', 'assistant_coach', 'analyst', 'player'];

const SuperAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [roleEdits, setRoleEdits] = useState({});
  const [teamEdits, setTeamEdits] = useState({});
  const [statusMsg, setStatusMsg] = useState('');
  const [uploadingTeam, setUploadingTeam] = useState('');
  const [showLogos, setShowLogos] = useState(false);
  const [userSortKey, setUserSortKey] = useState('');
  const [userSortDir, setUserSortDir] = useState('asc');
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [mediaTeamFilter, setMediaTeamFilter] = useState('');
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [approvalTeams, setApprovalTeams] = useState({});
  const [requestStatusMsg, setRequestStatusMsg] = useState('');
  const [teamStatus, setTeamStatus] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [deletingTeamId, setDeletingTeamId] = useState('');
  const [teamFormId, setTeamFormId] = useState('');
  const [teamFormName, setTeamFormName] = useState('');
  const [teamFormOwner, setTeamFormOwner] = useState('');
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [ownerUpdatingTeam, setOwnerUpdatingTeam] = useState('');
  const [removingUserId, setRemovingUserId] = useState('');
  const [addingUserTeamId, setAddingUserTeamId] = useState('');
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingSubs, setBillingSubs] = useState([]);
  const [seedingBilling, setSeedingBilling] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const kpis = useMemo(() => ([
    { label: 'Total Users', value: users.length || '—' },
    { label: 'Total Teams', value: teams.length || '—' },
    { label: 'Submissions (24h)', value: '—' },
    { label: 'Pending Issues', value: '—' },
  ]), [users.length, teams.length]);

  const auditPlaceholder = [
    { action: 'Set role to admin', who: 'superadmin', when: 'just now' },
    { action: 'Deleted submission', who: 'superadmin', when: '10m ago' },
  ];

  const loadUsers = async () => {
    setUsersLoading(true);
    setStatusMsg('');
    try {
      const snap = await getDocs(collection(db, 'users'));
      const next = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(next);
    } catch (err) {
      console.error('Failed to load users', err);
      setStatusMsg('Failed to load users.');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadTeams = async () => {
    setTeamsLoading(true);
    try {
      const snap = await getDocs(collection(db, 'teams'));
      const next = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTeams(next);
    } catch (err) {
      console.error('Failed to load teams', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  const loadRequests = async () => {
    setRequestsLoading(true);
    try {
      const snap = await getDocs(collection(db, 'teamJoinRequests'));
      const next = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRequests(next);
    } catch (err) {
      console.error('Failed to load requests', err);
      setRequestStatusMsg('Failed to load requests.');
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await signOut(auth);
      window.location.href = '/login';
    } catch (err) {
      console.error('Failed to sign out', err);
    } finally {
      setSigningOut(false);
    }
  };

  const loadBilling = async () => {
    const fallbackSummary = {
      totalRevenue: '$0',
      mrr: '$0',
      arr: '$0',
      activeSubs: 0,
      trialing: 0,
      churned: 0,
      updatedAt: null,
    };
    try {
      const summaryRef = doc(db, 'billingSummary', 'main');
      const summarySnap = await getDoc(summaryRef);
      if (summarySnap.exists()) {
        setBillingSummary(summarySnap.data());
      } else {
        setBillingSummary(fallbackSummary);
      }

      const subsSnap = await getDocs(query(collection(db, 'subscriptions'), limit(6)));
      const subs = subsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setBillingSubs(subs);
    } catch (err) {
      console.error('Failed to load billing', err);
      setBillingSummary(fallbackSummary);
      setBillingSubs([]);
    }
  };

  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'tickets'), orderBy('updatedAt', 'desc'), limit(20)));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTickets(list);
    } catch (err) {
      console.error('Failed to load tickets', err);
    } finally {
      setTicketsLoading(false);
    }
  };

  const seedBillingData = async () => {
    const now = new Date();
    const mockSummary = {
      totalRevenue: '$4,250',
      mrr: '$1,050',
      arr: '$12,600',
      activeSubs: 7,
      trialing: 2,
      churned: 1,
      updatedAt: serverTimestamp(),
      mockedAt: now.toISOString(),
    };
    const mockSubs = [
      { id: 'sub-1', teamId: 's8ul', planId: 'pro', status: 'active', renewsAt: serverTimestamp() },
      { id: 'sub-2', teamId: 'Octane', planId: 'standard', status: 'trialing', renewsAt: serverTimestamp() },
      { id: 'sub-3', teamId: 'Glory', planId: 'standard', status: 'active', renewsAt: serverTimestamp() },
      { id: 'sub-4', teamId: 'TLE', planId: 'pro', status: 'active', renewsAt: serverTimestamp() },
    ];

    setSeedingBilling(true);
    try {
      await setDoc(doc(db, 'billingSummary', 'main'), mockSummary, { merge: true });
      for (const sub of mockSubs) {
        await setDoc(doc(db, 'subscriptions', sub.id), sub, { merge: true });
      }
      await loadBilling();
    } catch (err) {
      console.error('Failed to seed billing data', err);
    } finally {
      setSeedingBilling(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadTeams();
    loadMedia();
    loadRequests();
    loadBilling();
    loadTickets();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (!term) return users;
    return users.filter(u =>
      (u.email || '').toLowerCase().includes(term) ||
      (u.ign || '').toLowerCase().includes(term) ||
      (u.role || '').toLowerCase().includes(term)
    );
  }, [userSearch, users]);

  const filteredTeams = useMemo(() => {
    const term = teamSearch.trim().toLowerCase();
    if (!term) return teams;
    return teams.filter(t =>
      (t.name || t.teamName || t.id || '').toLowerCase().includes(term)
    );
  }, [teamSearch, teams]);

  const sortedUsers = useMemo(() => {
    if (!userSortKey) return filteredUsers;
    const list = [...filteredUsers];
    const getVal = (u) => {
      if (userSortKey === 'email') return (u.email || '').toLowerCase();
      if (userSortKey === 'role') return (u.role || '').toLowerCase();
      if (userSortKey === 'team') return ((u.teamId || u.team || '')).toLowerCase();
      return '';
    };
    list.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return userSortDir === 'asc' ? -1 : 1;
      if (av > bv) return userSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredUsers, userSortDir, userSortKey]);

  const toggleUserSort = (key) => {
    const nextDir =
      userSortKey === key && userSortDir === 'asc' ? 'desc' : 'asc';
    setUserSortKey(key);
    setUserSortDir(nextDir);
  };

  const teamMembersById = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      const tid = u.teamId || u.team;
      if (!tid) return;
      if (!map[tid]) map[tid] = [];
      map[tid].push(u);
    });
    return map;
  }, [users]);

  const usersById = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  const handleRoleSelect = (userId, role) => {
    setRoleEdits(prev => ({ ...prev, [userId]: role }));
  };

  const applyRole = async (userId) => {
    const newRole = roleEdits[userId];
    if (!newRole) return;
    setStatusMsg('');
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setStatusMsg(`Role updated to "${newRole}" for user ${userId}.`);
    } catch (err) {
      console.error('Failed to update role', err);
      setStatusMsg('Failed to update role.');
    }
  };

  const handleTeamSelect = (userId, teamId) => {
    setTeamEdits(prev => ({ ...prev, [userId]: teamId }));
  };

  const applyTeam = async (userId) => {
    const newTeamId = teamEdits[userId] || null;
    setStatusMsg('');
    try {
      await updateDoc(doc(db, 'users', userId), { teamId: newTeamId });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, teamId: newTeamId } : u));
      setStatusMsg(`Team set to "${newTeamId || 'none'}" for user ${userId}.`);
    } catch (err) {
      console.error('Failed to update team', err);
      setStatusMsg('Failed to update team.');
    }
  };

  const toggleBetaApproval = async (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    const newApprovalStatus = !user.betaApproved;
    setStatusMsg('');
    try {
      await updateDoc(doc(db, 'users', userId), { 
        betaApproved: newApprovalStatus,
        betaApprovedAt: newApprovalStatus ? serverTimestamp() : null
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, betaApproved: newApprovalStatus } : u));
      const userEmail = user.email || userId;
      setStatusMsg(`${newApprovalStatus ? '✅ Approved' : '❌ Unapproved'} ${userEmail} for beta access.`);
    } catch (err) {
      console.error('Failed to update beta approval', err);
      setStatusMsg('Failed to update beta approval.');
    }
  };

  const handleTeamFormChange = (field, value) => {
    if (field === 'id') setTeamFormId(value);
    if (field === 'name') setTeamFormName(value);
    if (field === 'owner') setTeamFormOwner(value);
  };

  const formatTeamName = (team) => team.name || team.teamName || team.id || '-';
  const formatOwner = (team) => {
    const ownerId = team.owner || team.ownerId;
    if (!ownerId) return '-';
    const u = usersById[ownerId];
    if (u) {
      const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
      if (full) return full;
      if (u.ign) return u.ign;
      if (u.email) return u.email;
    }
    return ownerId;
  };
  const formatMembers = (team) => {
    const key = team.id || team.name || team.teamName;
    if (key && teamMembersById[key]) return teamMembersById[key].length;
    if (Array.isArray(team.members)) return team.members.length;
    if (typeof team.memberCount === 'number') return team.memberCount;
    return '-';
  };

  const handleCreateTeam = async () => {
    const name = teamFormName.trim();
    const explicitId = teamFormId.trim();
    if (!name) {
      setTeamStatus('Please enter a team name.');
      return;
    }

    const slugFromName = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const teamId = explicitId || slugFromName || `team-${Date.now()}`;

    setCreatingTeam(true);
    setTeamStatus('');
    try {
      const currentUid = auth?.currentUser?.uid || 'superadmin';
      await setDoc(
        doc(db, 'teams', teamId),
        {
          name,
          owner: teamFormOwner.trim() || currentUid,
          createdAt: serverTimestamp(),
          createdBy: currentUid,
        },
        { merge: true }
      );

      setTeamStatus(`Team "${name}" created.`);
      setTeamFormId('');
      setTeamFormName('');
      setTeamFormOwner('');
      await loadTeams();
    } catch (err) {
      console.error('Failed to create team', err);
      setTeamStatus('Failed to create team.');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!teamId) return;
    const memberCount = (teamMembersById[teamId] || []).length;
    const confirmMsg =
      memberCount > 0
        ? `Delete team ${teamId}? It currently has ${memberCount} member(s) linked.`
        : `Delete team ${teamId}?`;
    if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return;

    setDeletingTeamId(teamId);
    setTeamStatus('');
    try {
      await deleteDoc(doc(db, 'teams', teamId));
      setTeams((prev) => prev.filter((t) => t.id !== teamId));
      setTeamStatus(`Team ${teamId} deleted.`);
    } catch (err) {
      console.error('Failed to delete team', err);
      setTeamStatus('Failed to delete team.');
    } finally {
      setDeletingTeamId('');
    }
  };

  const handleSetOwner = async (teamId, userId) => {
    if (!teamId || !userId) return;
    setOwnerUpdatingTeam(teamId);
    setTeamStatus('');
    try {
      await updateDoc(doc(db, 'teams', teamId), { owner: userId });

      const members = teamMembersById[teamId] || [];
      await Promise.all(
        members.map((m) =>
          updateDoc(doc(db, 'users', m.id), { isOwner: m.id === userId })
        )
      );

      setTeamStatus(`Set ${userId} as owner of ${teamId}.`);
      await Promise.all([loadTeams(), loadUsers()]);
    } catch (err) {
      console.error('Failed to set owner', err);
      setTeamStatus('Failed to set owner.');
    } finally {
      setOwnerUpdatingTeam('');
    }
  };

  const handleRemoveUserFromTeam = async (teamId, userId) => {
    if (!teamId || !userId) return;
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Remove this user from the team?');
      if (!confirmed) return;
    }
    setRemovingUserId(userId);
    setTeamStatus('');
    try {
      await updateDoc(doc(db, 'users', userId), { teamId: null, isOwner: false });

      // If this user was the owner, clear owner on the team doc
      const team = teams.find((t) => t.id === teamId);
      if (team?.owner === userId) {
        await updateDoc(doc(db, 'teams', teamId), { owner: null });
        setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, owner: null } : t)));
      }

      // Update local users state so UI refreshes immediately
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, teamId: null, isOwner: false } : u))
      );

      setTeamStatus('User removed from team.');
    } catch (err) {
      console.error('Failed to remove user', err);
      setTeamStatus('Failed to remove user.');
    } finally {
      setRemovingUserId('');
    }
  };

  const handleAddUserToTeam = async (teamId, userId) => {
    if (!teamId || !userId) return;
    const user = users.find((u) => u.id === userId);
    if (!user) {
      setTeamStatus('User not found. Use email or UID.');
      return;
    }

    setAddingUserTeamId(teamId);
    setTeamStatus('');
    try {
      await updateDoc(doc(db, 'users', user.id), { teamId, isOwner: false });
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, teamId, isOwner: false } : u))
      );
      setTeamStatus(`Added ${user.email || user.id} to team ${teamId}.`);
    } catch (err) {
      console.error('Failed to add user to team', err);
      setTeamStatus('Failed to add user to team.');
    } finally {
      setAddingUserTeamId('');
    }
  };

  const handleTeamLogoChange = async (teamId, file) => {
    if (!file || !teamId) return;
    setUploadingTeam(teamId);
    setTeamStatus('');
    try {
      const ext = file.name.split('.').pop() || 'png';
      const storagePath = `team-logos/${teamId}-${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'teams', teamId), { logo: url });
      setTeams(prev => prev.map(t => t.id === teamId ? { ...t, logo: url } : t));
      setTeamStatus(`Logo updated for team ${teamId}.`);
    } catch (err) {
      console.error('Failed to upload logo', err);
      setTeamStatus('Failed to upload logo.');
    } finally {
      setUploadingTeam('');
    }
  };

  const generateCode = () => `JOIN-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

  const approveRequest = async (req) => {
    const targetTeamId = approvalTeams[req.id] || req.requestedTeamId || '';
    if (!targetTeamId) {
      setRequestStatusMsg('Select a team before approving.');
      return;
    }
    const code = generateCode();
    const currentUser = auth?.currentUser;
    try {
      await setDoc(doc(db, 'teamJoinCodes', code), {
        teamId: targetTeamId,
        createdBy: currentUser?.uid || 'superadmin',
        createdAt: serverTimestamp(),
        used: false,
        requestId: req.id,
        requestedTeamName: req.requestedTeamName || '',
        userId: req.userId || '',
      });

      await updateDoc(doc(db, 'teamJoinRequests', req.id), {
        status: 'approved',
        code,
        targetTeamId,
        approvedAt: serverTimestamp(),
      });

      setRequestStatusMsg(`Approved and issued code ${code}`);
      await loadRequests();
    } catch (err) {
      console.error('Failed to approve request', err);
      setRequestStatusMsg('Failed to approve request.');
    }
  };

  const denyRequest = async (reqId) => {
    try {
      await updateDoc(doc(db, 'teamJoinRequests', reqId), {
        status: 'denied',
        deniedAt: serverTimestamp(),
      });
      setRequestStatusMsg('Request denied.');
      await loadRequests();
    } catch (err) {
      console.error('Failed to deny request', err);
      setRequestStatusMsg('Failed to deny request.');
    }
  };

  const loadMedia = async () => {
    setMediaLoading(true);
    try {
      const snap = await getDocs(collection(db, 'mediaLibrary'));
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMediaItems(items);
    } catch (err) {
      console.error('Failed to load media library', err);
    } finally {
      setMediaLoading(false);
    }
  };

  const handleMediaUpload = async ({ files, type, teamId, playerName }) => {
    const fileList = (files || []).filter(Boolean);
    if (fileList.length === 0 || !type) {
      setStatusMsg('Choose a type and at least one PNG to upload.');
      return;
    }
    const currentUser = auth?.currentUser;
    const uploader = currentUser ? currentUser.uid : 'unknown';
    setStatusMsg('');
    setMediaLoading(true);
    try {
      for (const file of fileList) {
        const ext = (file.name.split('.').pop() || 'png').toLowerCase();
        if (ext !== 'png') {
          continue; // skip non-png
        }
        const folder = type === 'player' ? 'player-pictures' : 'team-logos';
        const teamSegment = teamId || 'general';
        const storagePath = `${folder}/${teamSegment}/${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        await addDoc(collection(db, 'mediaLibrary'), {
          type,
          teamId: teamId || null,
          playerName: type === 'player' ? playerName || file.name : null,
          url,
          path: storagePath,
          uploadedBy: uploader,
          uploadedAt: serverTimestamp(),
        });

        // Also write to team assets if a team is specified
        if (teamId) {
          const assetsRef = doc(db, `teams/${teamId}/assets/selected`);
          const assetsSnap = await getDoc(assetsRef);
          const current = assetsSnap.exists() ? assetsSnap.data() || {} : {};
          if (type === 'team') {
            await setDoc(
              assetsRef,
              {
                logo: url,
                updatedAt: serverTimestamp(),
                sourceTeam: teamId,
              },
              { merge: true }
            );
          } else if (type === 'player') {
            const existing = Array.isArray(current.players) ? current.players : [];
            await setDoc(
              assetsRef,
              {
                players: [
                  ...existing,
                  {
                    name: playerName || file.name,
                    avatar: url,
                  },
                ],
                updatedAt: serverTimestamp(),
                sourceTeam: teamId,
              },
              { merge: true }
            );
          }
        }
      }

      setStatusMsg('Media uploaded.');
      await loadMedia();
    } catch (err) {
      console.error('Failed to upload media', err);
      setStatusMsg('Failed to upload media.');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleMediaDelete = async (item) => {
    const { id, path, type, teamId, url } = item || {};
    setMediaLoading(true);
    try {
      // If storage path is provided, attempt to delete the file
      if (path) {
        try {
          const { deleteObject } = await import('firebase/storage');
          const storageRef = ref(storage, path);
          await deleteObject(storageRef);
        } catch (e) {
          // ignore storage errors
        }
      }
      if (id) {
        await deleteDoc(doc(db, 'mediaLibrary', id));
      }

      // Clean up team assets if applicable
      if (teamId) {
        const assetsRef = doc(db, `teams/${teamId}/assets/selected`);
        const assetsSnap = await getDoc(assetsRef);
        if (assetsSnap.exists()) {
          const assets = assetsSnap.data() || {};
          let changed = false;
          if (type === 'team') {
            if (assets.logo && assets.logo === url) {
              assets.logo = null;
              changed = true;
            }
          }
          if (type === 'player' && Array.isArray(assets.players)) {
            const filtered = assets.players.filter(
              (p) => (p.avatar || p.url) !== url
            );
            if (filtered.length !== assets.players.length) {
              assets.players = filtered;
              changed = true;
            }
          }
          if (changed) {
            await setDoc(assetsRef, { ...assets, updatedAt: serverTimestamp() }, { merge: true });
          }
        }
      }

      await loadMedia();
    } catch (err) {
      console.error('Failed to delete media', err);
      setStatusMsg('Failed to delete media.');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleBundleUpload = async ({ teamId, teamName, logoFile, players, updateId }) => {
    setStatusMsg('');
    setMediaLoading(true);
    try {
      const uploader = auth?.currentUser?.uid || 'unknown';

      let existingData = null;
      let targetRef = null;
      if (updateId) {
        targetRef = doc(db, 'mediaLibrary', updateId);
        const existingSnap = await getDoc(targetRef);
        if (existingSnap.exists()) {
          existingData = existingSnap.data();
        }
      }

      if (!logoFile && !existingData?.logo) {
        setStatusMsg('Team logo is required.');
        setMediaLoading(false);
        return;
      }

      const effectiveTeamId = teamId || existingData?.teamId || null;
      const effectiveTeamName = teamName || existingData?.teamName || effectiveTeamId || 'Unnamed Team';
      const teamSegment = (effectiveTeamId || effectiveTeamName || 'general')
        .toString()
        .replace(/\s+/g, '-')
        .toLowerCase();

      let logoUrl = existingData?.logo || null;
      let logoPath = existingData?.logoPath || null;
      if (logoFile) {
        if (logoPath) {
          try {
            const { deleteObject } = await import('firebase/storage');
            await deleteObject(ref(storage, logoPath));
          } catch (_) {
            // ignore storage cleanup failures
          }
        }
        const newLogoPath = `team-logos/${teamSegment}/bundle-logo-${Date.now()}.png`;
        const logoRef = ref(storage, newLogoPath);
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
        logoPath = newLogoPath;
      }

      const basePlayers =
        (players && players.length > 0)
          ? players
          : existingData?.players || [];

      const finalPlayers = [];
      for (let idx = 0; idx < basePlayers.length; idx++) {
        const p = basePlayers[idx];
        const prev = Array.isArray(existingData?.players) ? existingData.players[idx] || {} : {};
        let avatar = prev.avatar || p.avatar || '';
        let path = prev.path || '';

        if (p.file) {
          if (prev.path) {
            try {
              const { deleteObject } = await import('firebase/storage');
              await deleteObject(ref(storage, prev.path));
            } catch (_) {
              // ignore storage cleanup failures
            }
          }
          const playerPath = `player-pictures/${teamSegment}/bundle-player-${idx + 1}-${Date.now()}.png`;
          const playerRef = ref(storage, playerPath);
          await uploadBytes(playerRef, p.file);
          avatar = await getDownloadURL(playerRef);
          path = playerPath;
        }

        if (!avatar) continue;
        finalPlayers.push({
          name: p.name || prev.name || `Player ${idx + 1}`,
          avatar,
          path,
        });
      }

      const payload = {
        type: 'team',
        teamId: effectiveTeamId,
        teamName: effectiveTeamName,
        logo: logoUrl,
        logoPath: logoPath || null,
        players: finalPlayers,
        uploadedBy: uploader,
        uploadedAt: updateId && existingData?.uploadedAt ? existingData.uploadedAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      let bundleId = updateId;
      if (updateId && targetRef) {
        await setDoc(targetRef, payload, { merge: true });
      } else {
        const docRef = await addDoc(collection(db, 'mediaLibrary'), payload);
        bundleId = docRef.id;
      }

      if (effectiveTeamId) {
        const assetsRef = doc(db, `teams/${effectiveTeamId}/assets/selected`);
        await setDoc(
          assetsRef,
          {
            logo: logoUrl,
            players: finalPlayers.map((p) => ({ name: p.name, avatar: p.avatar })),
            updatedAt: serverTimestamp(),
            sourceTeam: effectiveTeamId,
            sourceLibraryId: bundleId,
          },
          { merge: true }
        );
      }

      const successMsg = updateId ? 'Team bundle updated.' : 'Team bundle uploaded.';
      setStatusMsg(successMsg);
      toast.success(successMsg);
      await loadMedia();
    } catch (err) {
      console.error('Failed to upload team bundle', err);
      setStatusMsg('Failed to upload team bundle.');
      toast.error('Failed to upload team bundle.');
    } finally {
      setMediaLoading(false);
    }
  };

  const filteredMedia = useMemo(() => {
    return mediaItems.filter((m) => {
      if (mediaTypeFilter && m.type !== mediaTypeFilter) return false;
      if (mediaTeamFilter && (m.teamId || '') !== mediaTeamFilter) return false;
      return true;
    });
  }, [mediaItems, mediaTeamFilter, mediaTypeFilter]);

  const renderTab = () => {
    if (activeTab === 'overview') {
      return (
        <Overview
          kpis={kpis}
          billingSummary={billingSummary}
          subscriptions={billingSubs}
          onSeedBilling={seedBillingData}
          seeding={seedingBilling}
        />
      );
    }

    if (activeTab === 'users') {
      return (
        <Users
          users={sortedUsers}
          teams={teams}
          usersLoading={usersLoading}
          userSearch={userSearch}
          statusMsg={statusMsg}
          roleEdits={roleEdits}
          teamEdits={teamEdits}
          onSearchChange={setUserSearch}
          onReload={loadUsers}
          onRoleSelect={handleRoleSelect}
          onRoleApply={applyRole}
          onTeamSelect={handleTeamSelect}
          onTeamApply={applyTeam}
          onBetaApprovalToggle={toggleBetaApproval}
          roleOptions={ROLE_OPTIONS}
          formatTeamName={formatTeamName}
          sortKey={userSortKey}
          sortDir={userSortDir}
          onSort={toggleUserSort}
        />
      );
    }

    if (activeTab === 'teams') {
      return (
        <Teams
          teams={filteredTeams}
          allUsers={users}
          teamSearch={teamSearch}
          teamsLoading={teamsLoading}
          showLogos={showLogos}
          uploadingTeam={uploadingTeam}
          creatingTeam={creatingTeam}
          deletingTeamId={deletingTeamId}
          showCreateTeam={showCreateTeam}
          teamFormId={teamFormId}
          teamFormName={teamFormName}
          teamFormOwner={teamFormOwner}
          teamMembersById={teamMembersById}
          roleOptions={ROLE_OPTIONS}
          roleEdits={roleEdits}
          ownerUpdatingTeam={ownerUpdatingTeam}
          removingUserId={removingUserId}
          addingUserTeamId={addingUserTeamId}
          onSearchChange={setTeamSearch}
          onToggleLogos={() => setShowLogos(prev => !prev)}
          onToggleCreateForm={() => setShowCreateTeam(prev => !prev)}
          onReload={loadTeams}
          onUploadLogo={handleTeamLogoChange}
          onCreateFieldChange={handleTeamFormChange}
          onCreateTeam={handleCreateTeam}
          onDeleteTeam={handleDeleteTeam}
          onRoleSelect={handleRoleSelect}
          onRoleApply={applyRole}
          onSetOwner={handleSetOwner}
          onAddUser={handleAddUserToTeam}
          onRemoveUser={handleRemoveUserFromTeam}
          formatTeamName={formatTeamName}
          formatMembers={formatMembers}
          formatOwner={formatOwner}
          statusMsg={teamStatus}
        />
      );
    }

    if (activeTab === 'library') {
      return (
          <MediaLibrary
            media={filteredMedia}
            mediaLoading={mediaLoading}
            typeFilter={mediaTypeFilter}
            teamFilter={mediaTeamFilter}
            teams={teams}
            onTypeFilterChange={setMediaTypeFilter}
            onTeamFilterChange={setMediaTeamFilter}
            onReload={loadMedia}
            onUpload={handleMediaUpload}
            onUploadBundle={handleBundleUpload}
            onDelete={handleMediaDelete}
            statusMsg={statusMsg}
          />
      );
    }

    if (activeTab === 'requests') {
      return (
        <Requests
          requests={requests}
          requestsLoading={requestsLoading}
          teams={teams}
          approvalTeams={approvalTeams}
          onSelectTeam={(reqId, teamId) => setApprovalTeams(prev => ({ ...prev, [reqId]: teamId }))}
          onApprove={approveRequest}
          onDeny={denyRequest}
          statusMsg={requestStatusMsg}
          onReload={loadRequests}
        />
      );
    }

    if (activeTab === 'tickets') {
      return (
        <Tickets
          tickets={tickets}
          ticketsLoading={ticketsLoading}
          onReload={loadTickets}
          teams={teams}
          onUpdateStatus={async (ticketId, status) => {
            try {
              await updateDoc(doc(db, 'tickets', ticketId), { status, updatedAt: serverTimestamp() });
              await loadTickets();
            } catch (err) {
              console.error('Failed to update ticket status', err);
            }
          }}
        />
      );
    }

    if (activeTab === 'audit') {
      return <Audit auditEntries={auditPlaceholder} />;
    }

    return null;
  };

  return (
    <>
      <TitleSetter title="Super Admin" />
      <div className="sa-container">
        <div className="sa-header">
          <div className="sa-brand">
            <img src="/assets/theboxlogo.png" alt="TheBox Logo" className="sa-logo" />
            <div>
              <p className="sa-eyebrow">Superadmin Console</p>
              <h1>System Management</h1>
            </div>
          </div>
          <div className="sa-tabs">
            {TABS.map((tab) => (
              <button
                key={tab}
                className={`sa-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="sa-content">{renderTab()}</div>

        <div className="sa-footer-note">
          <p>Note: Some actions may be placeholders; ensure Firestore/claims rules are aligned.</p>
          <button
            className="sa-btn ghost"
            onClick={handleLogout}
            disabled={signingOut}
            style={{ marginTop: '12px' }}
          >
            {signingOut ? 'Signing out...' : 'Logout'}
          </button>
        </div>
      </div>
    </>
  );
};

export default SuperAdmin;
